'use client';

// https://21st.dev/ibelick/cursor/with-spring
import { c as _c } from "react/compiler-runtime";
import { m, SpringOptions, useMotionValue, useSpring, AnimatePresence, Transition, Variant } from 'motion/react';
import React, { useEffect, useState, useRef, useEffectEvent } from 'react';
import { cn } from '@/lib/utils';
type CursorProps = {
  children: React.ReactNode;
  className?: string;
  springConfig?: SpringOptions;
  attachToParent?: boolean;
  transition?: Transition;
  variants?: {
    initial: Variant;
    animate: Variant;
    exit: Variant;
  };
  onPositionChange?: (x: number, y: number) => void;
};
export function Cursor(t0) {
  const $ = _c(39);
  const {
    children,
    className,
    springConfig,
    attachToParent,
    variants,
    transition,
    onPositionChange
  } = t0;
  const cursorX = useMotionValue(0);
  const cursorY = useMotionValue(0);
  const cursorRef = useRef(null);
  const [isVisible, setIsVisible] = useState(!attachToParent);
  const [isMounted, setIsMounted] = useState(false);
  let t1;
  if ($[0] !== onPositionChange) {
    t1 = (x, y) => {
      onPositionChange?.(x, y);
    };
    $[0] = onPositionChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const reportPosition = useEffectEvent(t1);
  let t2;
  let t3;
  if ($[2] !== cursorX || $[3] !== cursorY) {
    t2 = () => {
      setIsMounted(true);
      if (typeof window !== "undefined") {
        cursorX.set(window.innerWidth / 2);
        cursorY.set(window.innerHeight / 2);
      }
    };
    t3 = [cursorX, cursorY];
    $[2] = cursorX;
    $[3] = cursorY;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== attachToParent || $[7] !== cursorX || $[8] !== cursorY || $[9] !== reportPosition) {
    t4 = () => {
      if (!attachToParent) {
        document.body.style.cursor = "none";
      } else {
        document.body.style.cursor = "auto";
      }
      const updatePosition = e => {
        cursorX.set(e.clientX);
        cursorY.set(e.clientY);
        reportPosition(e.clientX, e.clientY);
      };
      document.addEventListener("mousemove", updatePosition, {
        passive: true
      });
      return () => {
        document.removeEventListener("mousemove", updatePosition);
      };
    };
    $[6] = attachToParent;
    $[7] = cursorX;
    $[8] = cursorY;
    $[9] = reportPosition;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== attachToParent || $[12] !== cursorX || $[13] !== cursorY) {
    t5 = [cursorX, cursorY, attachToParent];
    $[11] = attachToParent;
    $[12] = cursorX;
    $[13] = cursorY;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  useEffect(t4, t5);
  let t6;
  if ($[15] !== springConfig) {
    t6 = springConfig || {
      duration: 0
    };
    $[15] = springConfig;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const cursorXSpring = useSpring(cursorX, t6);
  let t7;
  if ($[17] !== springConfig) {
    t7 = springConfig || {
      duration: 0
    };
    $[17] = springConfig;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const cursorYSpring = useSpring(cursorY, t7);
  let t8;
  let t9;
  if ($[19] !== attachToParent) {
    t8 = () => {
      if (!attachToParent || !cursorRef.current) {
        return;
      }
      const parentElement = cursorRef.current.parentElement;
      if (!parentElement) {
        return;
      }
      const handleEnter = () => {
        parentElement.style.cursor = "none";
        setIsVisible(true);
      };
      const handleLeave = () => {
        parentElement.style.cursor = "auto";
        setIsVisible(false);
      };
      parentElement.addEventListener("mouseenter", handleEnter);
      parentElement.addEventListener("mouseleave", handleLeave);
      return () => {
        parentElement.removeEventListener("mouseenter", handleEnter);
        parentElement.removeEventListener("mouseleave", handleLeave);
        if (parentElement.style.cursor === "none") {
          parentElement.style.cursor = "auto";
        }
      };
    };
    t9 = [attachToParent];
    $[19] = attachToParent;
    $[20] = t8;
    $[21] = t9;
  } else {
    t8 = $[20];
    t9 = $[21];
  }
  useEffect(t8, t9);
  let t10;
  if ($[22] !== cursorXSpring || $[23] !== cursorYSpring || $[24] !== isMounted) {
    t10 = isMounted ? {
      x: cursorXSpring,
      y: cursorYSpring,
      translateX: "-50%",
      translateY: "-50%"
    } : {
      translateX: "-50%",
      translateY: "-50%"
    };
    $[22] = cursorXSpring;
    $[23] = cursorYSpring;
    $[24] = isMounted;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const cursorStyle = t10;
  let t11;
  if ($[26] !== className) {
    t11 = cn("pointer-events-none fixed top-0 left-0 z-50", className);
    $[26] = className;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== children || $[29] !== isVisible || $[30] !== transition || $[31] !== variants) {
    t12 = isVisible && <m.div initial="initial" animate="animate" exit="exit" variants={variants} transition={transition}>{children}</m.div>;
    $[28] = children;
    $[29] = isVisible;
    $[30] = transition;
    $[31] = variants;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== t12) {
    t13 = <AnimatePresence>{t12}</AnimatePresence>;
    $[33] = t12;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== cursorStyle || $[36] !== t11 || $[37] !== t13) {
    t14 = <m.div ref={cursorRef} className={t11} style={cursorStyle}>{t13}</m.div>;
    $[35] = cursorStyle;
    $[36] = t11;
    $[37] = t13;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  return t14;
}